import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import { useCage } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { isValidCageId } from '@/domain/cages';
import type { HealthParameterKey, HealthScoreMetrics } from '@/domain/health';
import { useGetHealthOverview } from '@/domain/health';
import { parseLocale } from '@/utils/localeFormatters';

import { findHealthOverviewParameter } from '../../_utils/healthDetailOverviewHelpers';
import HealthDetailChartSkeleton from '../HealthDetailChartSkeleton/HealthDetailChartSkeleton';
import HealthStatusPeriodPicker, {
    type HealthStatusPeriod,
} from '../HealthStatusPeriodPicker/HealthStatusPeriodPicker';

import { buildHealthStatusChartSpec, HEALTH_STATUS_SCORE_KEYS } from './healthStatusChartSpec';

import chartBlockStyles from '../HealthDetailChartContentBlock.module.scss';
import styles from './HealthStatusChart.module.scss';

export type { HealthStatusPeriod } from '../HealthStatusPeriodPicker/HealthStatusPeriodPicker';

export interface HealthStatusChartProps {
    healthParameterKey: HealthParameterKey;
}

export default function HealthStatusChart(props: HealthStatusChartProps) {
    const { healthParameterKey } = props;
    const { t, i18n } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();
    const locale = parseLocale(i18n.language);
    const { cageId } = useCage();
    const { daypartTag, selectedFishGroupId } = useCageFilter();
    const [period, setPeriod] = useState<HealthStatusPeriod>('daily');

    const overviewQueryParams = useMemo(
        () => ({ daypartTag, fishGroupId: selectedFishGroupId }),
        [daypartTag, selectedFishGroupId]
    );

    const {
        data: overview,
        isLoading,
        isFetching,
    } = useGetHealthOverview(cageId, overviewQueryParams, {
        enabled: isValidCageId(cageId),
        staleTime: 5 * 60 * 1000,
    });

    const loading = isLoading || isFetching;

    const parameter = useMemo(
        () => findHealthOverviewParameter(overview, healthParameterKey),
        [overview, healthParameterKey]
    );

    const periodMetrics: HealthScoreMetrics | null | undefined =
        period === 'weekly' ? parameter?.weekly : parameter?.daily;

    const hasNoData = !loading && periodMetrics == null;

    const scoreLabels = useMemo(
        () => HEALTH_STATUS_SCORE_KEYS.map((key) => tGlobal(`scores.${key}`)),
        [tGlobal]
    );
    const valueLabel = t('detail.common.shareOfFish');
    const spec = useMemo(
        () => buildHealthStatusChartSpec(periodMetrics, locale, scoreLabels, valueLabel),
        [periodMetrics, locale, scoreLabels, valueLabel]
    );
    const chart = useMetricChart(spec);

    return (
        <ContentBlock
            title={t('detail.common.statusTitle')}
            size="compact"
            spacing="none"
            fill
            className={`${chartBlockStyles.chartBlock} ${chartBlockStyles.fixedHeightChartBlock}`}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    picker={
                        <HealthStatusPeriodPicker
                            value={period}
                            onChange={setPeriod}
                            menuPosition="right"
                            disabled={loading}
                        />
                    }
                    chartRef={chart.chartRef}
                    chartName={`health-status-${healthParameterKey}-${period}`}
                    isFullScreen={chart.isFullScreen}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                    disabled={loading}
                />
            }>
            <div className={`${chartBlockStyles.chartBlockBody} ${styles.chartBody}`}>
                {loading ? (
                    <div className={styles.loading}>
                        <HealthDetailChartSkeleton />
                    </div>
                ) : hasNoData ? (
                    <EmptyState
                        title={t('detail.common.noData')}
                        appearance="quiet"
                        live="polite"
                        className={styles.noData}
                    />
                ) : (
                    <div className={chartBlockStyles.chartArea}>
                        <MetricChart
                            kind="bar"
                            data={chart.data}
                            options={chart.options}
                            legendItems={chart.legendItems}
                            visibleSeries={chart.visibleSeries}
                            onToggleSeries={chart.toggleSeries}
                            chartRef={chart.chartRef}
                            height="fill"
                        />
                    </div>
                )}
            </div>
        </ContentBlock>
    );
}
